import LegalPageLayout, { type LegalSection } from '../components/layout/LegalPageLayout';
import Seo from '../components/Seo';
import '../styles/legal-pages.css';

const sections: LegalSection[] = [
  { id: 'general', title: '1. Общие положения' },
  { id: 'status', title: '2. Статус лиц при обработке данных' },
  { id: 'executor', title: '3. Сведения об Исполнителе' },
  { id: 'data', title: '4. Состав персональных данных' },
  { id: 'purposes', title: '5. Цели обработки' },
  { id: 'bases', title: '6. Правовые основания' },
  { id: 'processing', title: '7. Действия по обработке' },
  { id: 'localization', title: '8. Локализация, хранение, сроки' },
  { id: 'security', title: '9. Меры защиты' },
  { id: 'subprocessors', title: '10. Субпроцессоры' },
  { id: 'incidents', title: '11. Инциденты ИБ' },
  { id: 'deletion', title: '12. Удаление данных по запросу' },
  { id: 'rights', title: '13. Права субъектов ПД' },
  { id: 'cookies', title: '14. Cookies и аналитика' },
  { id: 'liability', title: '15. Ответственность' },
  { id: 'changes', title: '16. Изменение Политики' },
  { id: 'contacts', title: '17. Контакты' },
  { id: 'mobile', title: '18. Мобильное приложение Android' },
];

export default function PrivacyPage() {
  return (
    <LegalPageLayout
      title="Политика обработки персональных данных"
      updatedAt="28 января 2026 г."
      version="1.0"
      sections={sections}
    >
      <Seo
        title="Политика обработки персональных данных — Recvisit"
        description="Политика обработки и защиты персональных данных пользователей и клиентов сервиса Recvisit в соответствии с 152‑ФЗ."
        canonicalPath="/privacy"
      />
      <section id="general" className="legal-section">
        <h2>1. Общие положения</h2>
        <p>
          Настоящая Политика обработки персональных данных (далее — <strong>Политика</strong>) определяет порядок
          обработки и защиты персональных данных пользователей сервиса <strong>Recvisit</strong> (далее —
          <strong> Сервис</strong>).
        </p>
        <p>
          Политика разработана в соответствии с Федеральным законом № 152‑ФЗ «О персональных данных» и иными нормативными
          актами Российской Федерации.
        </p>
      </section>

      <section id="status" className="legal-section">
        <h2>2. Статус лиц при обработке данных</h2>
        <p><strong>2.1. Оператор персональных данных:</strong></p>
        <ul>
          <li>в отношении персональных данных сотрудников Клиента — соответствующий работодатель (Клиент);</li>
          <li>в отношении данных владельцев аккаунтов/администраторов — Исполнитель.</li>
        </ul>
        <p>
          <strong>2.2.</strong> Исполнитель обрабатывает персональные данные сотрудников исключительно по поручению
          Клиента и не определяет цели и содержание такой обработки.
        </p>
        <p>
          <strong>2.3.</strong> Исполнитель не является работодателем, налоговым агентом или субъектом трудовых
          правоотношений с сотрудниками Клиента.
        </p>
        <p>
          <strong>2.4. Договор обработки персональных данных по поручению:</strong>
        </p>
        <p>
          В отношении персональных данных сотрудников Клиента Исполнитель является лицом, обрабатывающим персональные 
          данные по поручению оператора (Клиента) в соответствии со статьей 6 Федерального закона № 152-ФЗ «О персональных 
          данных».
        </p>
        <p>
          Условия обработки персональных данных по поручению определяются настоящим Соглашением и настоящей Политикой 
          конфиденциальности. Акцепт Пользовательского соглашения означает заключение договора обработки персональных 
          данных по поручению между Клиентом (оператором) и Исполнителем (обработчиком) на условиях, изложенных в указанных 
          документах.
        </p>
        <p>
          Исполнитель обязуется обрабатывать персональные данные сотрудников Клиента исключительно в целях, определенных 
          Клиентом, и в соответствии с его указаниями, а также обеспечивать конфиденциальность и безопасность обрабатываемых 
          данных.
        </p>
      </section>

      <section id="executor" className="legal-section">
        <h2>3. Сведения об Исполнителе</h2>
        <div className="legal-box">
          <p><strong>Индивидуальный предприниматель Шмелева Екатерина Владимировна</strong></p>
          <p>ИНН: 482306497385</p>
          <p>ОГРНИП: 324480000041882</p>
          <p>Адрес: Липецкая область, г. Липецк, ул. Советская, д. 4, оф. 619</p>
          <p>Email: <a href="mailto:info@recvi.ru">info@recvi.ru</a></p>
          <p>Сайт: <a href="https://recvi.ru" target="_blank" rel="noopener noreferrer">recvi.ru</a></p>
        </div>
        <p>
          Уведомление/включение в реестр операторов персональных данных Роскомнадзора осуществляется Исполнителем в
          случаях и порядке, предусмотренных законодательством Российской Федерации.
        </p>
      </section>

      <section id="data" className="legal-section">
        <h2>4. Состав персональных данных</h2>
        <h3>4.1. Данные Клиентов (владельцев аккаунтов/администраторов)</h3>
        <ul>
          <li>ФИО, должность (при наличии);</li>
          <li>контактные данные (email, телефон);</li>
          <li>реквизиты организации/ИП (ИНН, ОГРН/ОГРНИП, наименование/ФИО);</li>
          <li>учетные данные (логин, хэш пароля, токены/сессии);</li>
          <li>технические данные (IP‑адрес, cookies, User‑Agent, логи событий).</li>
        </ul>

        <h3>4.2. Данные сотрудников Клиента (загружаются/вносятся Клиентом)</h3>
        <ul>
          <li>ФИО, должность и иные кадровые данные, которые Клиент решит хранить в Сервисе;</li>
          <li>фото/изображения (если используются для идентификации/QR);</li>
          <li>события посещаемости/фиксации (время, объект, фото при сканировании — если включено);</li>
          <li>технические данные устройства, используемого при фиксации.</li>
        </ul>

        <p>
          <strong>4.3.</strong> Исполнитель не проверяет достоверность и законность предоставленных Клиентом данных.
          Клиент гарантирует наличие правовых оснований и необходимых согласий на передачу и обработку персональных
          данных сотрудников.
        </p>
      </section>

      <section id="purposes" className="legal-section">
        <h2>5. Цели обработки</h2>
        <p>Обработка персональных данных осуществляется исключительно в целях:</p>
        <ul>
          <li>исполнения договорных обязательств и предоставления доступа к Сервису;</li>
          <li>обеспечения функционирования и безопасности Сервиса;</li>
          <li>выполнения требований законодательства РФ.</li>
        </ul>
      </section>

      <section id="bases" className="legal-section">
        <h2>6. Правовые основания</h2>
        <p>Основания обработки персональных данных:</p>
        <ul>
          <li>договор (оферта/соглашение);</li>
          <li>согласие субъекта персональных данных (в применимых случаях);</li>
          <li>требования законодательства Российской Федерации.</li>
        </ul>
      </section>

      <section id="processing" className="legal-section">
        <h2>7. Действия по обработке</h2>
        <p>Исполнитель осуществляет (автоматизированно) следующие действия с персональными данными:</p>
        <ul>
          <li>сбор, запись, систематизация, накопление, хранение;</li>
          <li>уточнение (обновление, изменение), извлечение, использование;</li>
          <li>предоставление доступа (передача/доступ), обезличивание, блокирование;</li>
          <li>удаление, уничтожение.</li>
        </ul>
      </section>

      <section id="localization" className="legal-section">
        <h2>8. Локализация, хранение, сроки</h2>
        <ul>
          <li>обработка и хранение данных осуществляется на территории Российской Федерации;</li>
          <li>
            <strong>Сроки обработки основных данных:</strong> в течение срока действия договорных отношений между 
            Исполнителем и Клиентом, а также в течение 30 календарных дней после расторжения договора для возможности 
            восстановления доступа по запросу Клиента.
          </li>
          <li>
            <strong>Сроки хранения технических данных:</strong>
            <ul>
              <li>Логи событий и действий пользователей — 90 календарных дней</li>
              <li>IP-адреса и технические идентификаторы — 90 календарных дней</li>
              <li>Резервные копии данных — до 90 календарных дней после удаления основных данных</li>
            </ul>
          </li>
          <li>
            После истечения указанных сроков данные удаляются и/или обезличиваются, если иное не требуется законодательством 
            Российской Федерации (например, для целей налогового и бухгалтерского учета).
          </li>
          <li>
            Данные могут храниться дольше указанных сроков только в случаях, предусмотренных законодательством РФ 
            (например, по запросу правоохранительных органов).
          </li>
        </ul>
      </section>

      <section id="security" className="legal-section">
        <h2>9. Меры защиты</h2>
        <p>Исполнитель принимает необходимые организационные и технические меры, включая:</p>
        <ul>
          <li>контроль доступа к данным;</li>
          <li>использование защищенных каналов связи (HTTPS/TLS);</li>
          <li>резервное копирование (в пределах применимой архитектуры);</li>
          <li>мониторинг инцидентов и событий безопасности.</li>
        </ul>
        <p>
          Конкретные меры защиты могут изменяться Исполнителем в целях повышения безопасности без отдельного уведомления
          субъектов данных, если иное не предусмотрено законом.
        </p>
      </section>

      <section id="subprocessors" className="legal-section">
        <h2>10. Субпроцессоры</h2>
        <p>
          Для обеспечения функционирования Сервиса Исполнитель вправе привлекать третьих лиц (субпроцессоров), включая
          категории:
        </p>
        <ul>
          <li>хостинг‑провайдеры (территория РФ);</li>
          <li>почтовые сервисы (территория РФ);</li>
          <li>платежные системы (территория РФ);</li>
          <li>сервисы мониторинга и аналитики (например, Яндекс.Метрика — при наличии согласия на аналитику).</li>
        </ul>
        <p>
          Перечень субпроцессоров может публиковаться/обновляться Исполнителем без отдельного согласования, при условии
          соблюдения требований 152‑ФЗ.
        </p>
      </section>

      <section id="incidents" className="legal-section">
        <h2>11. Инциденты информационной безопасности</h2>
        <p>
          В случае выявления инцидента (утечка, несанкционированный доступ, утрата) Исполнитель принимает меры по
          локализации и устранению последствий и уведомляет Клиента в течение 24 часов с момента обнаружения по email,
          указанному при регистрации.
        </p>
        <p>
          При необходимости Исполнитель уведомляет уполномоченные органы в сроки, установленные законодательством РФ.
          Клиент как оператор персональных данных сотрудников самостоятельно уведомляет субъектов данных в порядке,
          предусмотренном законом.
        </p>
      </section>

      <section id="deletion" className="legal-section">
        <h2>12. Удаление персональных данных по запросу</h2>
        <p>
          Клиент вправе запросить удаление загруженных им данных, направив запрос на email{' '}
          <a href="mailto:info@recvi.ru">info@recvi.ru</a>. При необходимости Исполнитель вправе запросить подтверждение
          полномочий представителя.
        </p>
        <p>
          Срок исполнения запроса — до 10 рабочих дней, за исключением случаев, когда хранение отдельных данных требуется
          законом (например, учет/налоги/бухгалтерия и т.п.). Удаленные данные не подлежат восстановлению.
        </p>
      </section>

      <section id="rights" className="legal-section">
        <h2>13. Права субъектов персональных данных</h2>
        <p>Субъекты персональных данных вправе:</p>
        <ul>
          <li>получать информацию об обработке персональных данных;</li>
          <li>требовать уточнения или удаления данных в предусмотренных законом случаях;</li>
          <li>отзывать согласие (если обработка основана на согласии);</li>
          <li>обращаться с жалобами в Роскомнадзор.</li>
        </ul>
        <p>
          <strong>Порядок отзыва согласия на обработку персональных данных:</strong>
        </p>
        <ul>
          <li>
            Субъект персональных данных вправе отозвать согласие на обработку персональных данных, направив запрос на email{' '}
            <a href="mailto:info@recvi.ru">info@recvi.ru</a>.
          </li>
          <li>
            Отзыв согласия влечет прекращение обработки персональных данных и их удаление в срок до 30 календарных дней 
            с момента получения запроса, за исключением случаев, когда хранение данных требуется законодательством РФ.
          </li>
          <li>
            Отзыв согласия на обработку персональных данных владельца аккаунта может повлечь прекращение доступа к Сервису, 
            так как обработка данных необходима для предоставления услуг.
          </li>
          <li>
            В отношении персональных данных сотрудников Клиента отзыв согласия должен быть направлен оператору персональных 
            данных (Клиенту), который несет ответственность за уведомление Исполнителя о необходимости прекращения обработки.
          </li>
        </ul>
        <p>
          Обращения направляются по адресу <a href="mailto:info@recvi.ru">info@recvi.ru</a>. Срок рассмотрения — до 30
          календарных дней.
        </p>
      </section>

      <section id="cookies" className="legal-section">
        <h2>14. Cookies и аналитика</h2>
        <p>
          На сайте используются технические cookies (обязательные) и, при наличии согласия, аналитические cookies (в том
          числе Яндекс.Метрика). Отказ от аналитики не влияет на базовую работоспособность сайта.
        </p>
        <p>
          Подробная информация о cookies и настройках согласий размещается на странице{' '}
          <a href="/cookies">/cookies</a>.
        </p>
      </section>

      <section id="liability" className="legal-section">
        <h2>15. Ответственность</h2>
        <p>
          Исполнитель не несет ответственности за отсутствие у Клиента согласий и правовых оснований на обработку и
          передачу персональных данных сотрудников, за незаконную передачу персональных данных Клиентом, а также за
          нарушения трудового, налогового и иного законодательства Клиентом.
        </p>
      </section>

      <section id="changes" className="legal-section">
        <h2>16. Изменение Политики</h2>
        <p>
          Исполнитель вправе вносить изменения в Политику в одностороннем порядке. Актуальная редакция публикуется на
          сайте Сервиса.
        </p>
      </section>

      <section id="contacts" className="legal-section">
        <h2>17. Контакты</h2>
        <p>
          По вопросам обработки персональных данных: <a href="mailto:info@recvi.ru">info@recvi.ru</a>.
        </p>
        <p>
          Для жалоб в Роскомнадзор: сайт <a href="https://pd.rkn.gov.ru" target="_blank" rel="noopener noreferrer">pd.rkn.gov.ru</a>, телефон горячей линии
          8 (800) 707‑77‑07.
        </p>
      </section>

      <section id="mobile" className="legal-section">
        <h2>18. Мобильное приложение Android</h2>
        <p>
          Исполнитель предоставляет мобильное приложение <strong>Recvisit Kiosk</strong> для операционной системы Android,
          которое функционирует как веб-контейнер (WebView) для отображения и работы с PWA-версией Сервиса.
        </p>
        
        <h3>18.1. Используемые разрешения Android</h3>
        <p>
          Приложение запрашивает следующие разрешения для обеспечения полной функциональности PWA внутри WebView:
        </p>
        <ul>
          <li>
            <strong>CAMERA (Камера)</strong> — используется для сканирования QR-кодов сотрудниками через веб-интерфейс PWA, 
            если эта функция активирована Клиентом. Приложение само по себе не обрабатывает фотографии напрямую; доступ к камере 
            предоставляется исключительно веб-приложению (PWA) внутри WebView.
          </li>
          <li>
            <strong>ACCESS_FINE_LOCATION (Точное местоположение)</strong> — может использоваться PWA для привязки события 
            фиксации к географическим координатам (если включено Клиентом). Данные геолокации обрабатываются только в рамках 
            PWA и не передаются третьим лицам напрямую из нативного приложения.
          </li>
          <li>
            <strong>RECORD_AUDIO (Микрофон)</strong> — может использоваться PWA для голосовых функций или дополнительных 
            возможностей интерфейса (если будут реализованы в будущем). В текущей версии данное разрешение зарезервировано для 
            совместимости с будущими функциями PWA.
          </li>
        </ul>
        
        <h3>18.2. Цели использования разрешений</h3>
        <p>
          Все перечисленные разрешения необходимы исключительно для корректной работы веб-функций PWA внутри мобильного 
          приложения. Нативное Android-приложение не собирает, не обрабатывает и не передает персональные данные независимо 
          от PWA. Вся обработка данных осуществляется в соответствии с настоящей Политикой и происходит на серверах Сервиса.
        </p>
        
        <h3>18.3. Управление разрешениями</h3>
        <p>
          Пользователь (сотрудник или администратор) может в любой момент отозвать предоставленные разрешения через настройки 
          Android-устройства: <strong>Настройки → Приложения → Recvisit Kiosk → Разрешения</strong>. Отзыв разрешений может 
          ограничить доступность отдельных функций Сервиса, требующих работы с камерой, геолокацией или микрофоном.
        </p>
        
        <h3>18.4. Обновления приложения</h3>
        <p>
          Мобильное приложение может получать обновления через Google Play (частное распространение в рамках Managed Google Play). 
          Обновления могут включать исправления ошибок, улучшение производительности и добавление новых функций. Существенные 
          изменения в обработке персональных данных будут отражены в обновлениях настоящей Политики.
        </p>
      </section>
    </LegalPageLayout>
  );
}

